import { memo } from 'react';
import { Button } from '@mantine/core';
import { IconFilterOff } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';
import { ICON_SIZE_SM } from '@/constants/ui';

export interface ClearFiltersButtonProps {
  onClick: () => void;
  fullWidth?: boolean;
}

/**
 * Standard "clear all filters" button shared across filter panels.
 *
 * Fixed canonical look (subtle / gray / IconFilterOff + the `filters.clearAllFilters`
 * label) so every panel's reset affordance is visually identical. Alignment stays
 * the consumer's responsibility — wrap in `.clearRow`, `<Group>` or pass `fullWidth`.
 *
 * @example Right-aligned (TaskFilters / NoteFilters / ObstructionFilters)
 * <div className={styles.clearRow}>
 *   <ClearFiltersButton onClick={resetFilters} />
 * </div>
 *
 * @example Full-width (RelatedTasksCore)
 * <ClearFiltersButton onClick={clearFilters} fullWidth />
 */
export const ClearFiltersButton = memo(function ClearFiltersButton({
  onClick,
  fullWidth = false,
}: ClearFiltersButtonProps) {
  const { t } = useTranslation();

  return (
    <Button
      variant="subtle"
      color="gray"
      size="sm"
      fullWidth={fullWidth}
      leftSection={<IconFilterOff size={ICON_SIZE_SM} />}
      onClick={onClick}
    >
      {t('filters.clearAllFilters')}
    </Button>
  );
});
